import { motion } from 'framer-motion';
import { Bell, BarChart3, Users } from 'lucide-react';
import { LANDING } from '@/constants/testIds';
import { ASSETS } from '@/lib/assets';
import Bottle360 from '@/components/Bottle360';

const features = [
  { Icon: Bell, label: 'Daily Reminders', sub: 'Gentle nudges. Never naggy.' },
  { Icon: BarChart3, label: 'Progress Tracking', sub: 'Streaks that build themselves.' },
  { Icon: Users, label: 'Community Challenges', sub: 'Hydrate together. Win together.' },
];

export default function Challenge() {
  return (
    <section
      id="challenge"
      className="relative bg-[#0E0E0E] text-[#FAF8F4] overflow-hidden py-32 lg:py-44"
    >
      {/* Ambient orbs */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-1/3 left-1/4 w-[40vw] h-[40vw] rounded-full bg-[#CBBBA0]/8 blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[30vw] h-[30vw] rounded-full bg-[#CBBBA0]/6 blur-[120px]" />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        {/* Bottle (top view) + QR */}
        <div className="lg:col-span-6 relative flex items-center justify-center order-2 lg:order-1">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 1.2 }}
            className="relative h-[60vh] min-h-[420px] w-full max-w-[520px]"
          >
            {/* Inline 360 viewer — auto-orbits, drag to take control */}
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute inset-0"
            >
              <Bottle360
                front={ASSETS.heroBlackMarble}
                back={ASSETS.backAngleBlack}
                top={ASSETS.topAngleBlack}
                accent="#CBBBA0"
                hintColor="rgba(250,248,244,0.6)"
                className="w-full h-full"
                testId="challenge-bottle-360"
              />
            </motion.div>

            {/* Animated QR */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -right-2 sm:-right-8 top-1/4 bg-[#FAF8F4] p-3 rounded-xl shadow-2xl shadow-black/60 z-20"
              data-testid={LANDING.challengeQR}
            >
              <div className="relative w-28 h-28 grid grid-cols-8 grid-rows-8 gap-[2px]">
                {Array.from({ length: 64 }).map((_, i) => {
                  const on = ((i * 73 + 11) % 7) % 2 === 0;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: on ? 1 : 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.008 }}
                      className={on ? 'bg-[#111]' : 'bg-transparent'}
                    />
                  );
                })}
                {['top-0 left-0', 'top-0 right-0', 'bottom-0 left-0'].map((p) => (
                  <div
                    key={p}
                    className={`absolute ${p} w-7 h-7 border-[3px] border-[#111] flex items-center justify-center`}
                  >
                    <div className="w-2 h-2 bg-[#111]" />
                  </div>
                ))}
              </div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#111] mt-2 text-center">
                Scan to Begin
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Text */}
        <div className="lg:col-span-6 order-1 lg:order-2">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="text-[11px] tracking-[0.35em] uppercase text-[#CBBBA0] mb-6"
          >
            Chapter 04 · The Challenge
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
            className="font-display text-[clamp(2.5rem,6vw,5rem)] leading-[0.95] tracking-[-0.03em]"
          >
            Build Better
            <br />
            <span className="font-serif italic text-[#CBBBA0]">Habits.</span>
          </motion.h2>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
            className="mt-8 flex items-center gap-6 text-[18px] font-serif italic text-[#FAF8F4]/80"
          >
            <span>Scan.</span>
            <span className="h-px w-6 bg-[#FAF8F4]/30" />
            <span>Track.</span>
            <span className="h-px w-6 bg-[#FAF8F4]/30" />
            <span>Streak.</span>
          </motion.div>

          <p className="mt-6 max-w-md text-[15px] leading-relaxed text-[#FAF8F4]/60">
            Every KIVO bottle ships with a private QR. Scan once, and your tumbler quietly
            tracks your hydration streak — the way habits were always meant to grow.
          </p>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-6">
            {features.map(({ Icon, label, sub }, i) => (
              <motion.div
                key={label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.8, delay: 0.1 + i * 0.1 }}
                className="border-t border-[#FAF8F4]/15 pt-5"
              >
                <Icon size={20} strokeWidth={1.4} className="text-[#CBBBA0] mb-3" />
                <div className="font-display text-[16px] text-[#FAF8F4]">{label}</div>
                <div className="text-[12px] text-[#FAF8F4]/55 mt-1">{sub}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
